import type {
  FlexConstraintValue,
  UsePanelResizeParams,
} from "./usePanelResize.types";

/**
 * Parses a pixel string and returns the numeric value
 * Examples: "300px" -> 300, "800px" -> 800
 * Returns null if not a valid pixel value
 */
export const parsePixelValue = (value: string): number | null => {
  const match = value.trim().match(/^(-?\d+\.?\d*)px$/i);
  if (!match) return null;
  return parseFloat(match[1]);
};

export type ParsedConstraint = { value: number; isPixel: boolean };

/**
 * Extracts numeric value from a constraint
 * - If number: returns as-is (flex-grow)
 * - If string: parses pixel value
 */
export const getConstraintValue = (
  constraint: FlexConstraintValue | undefined
): ParsedConstraint | null => {
  if (constraint === undefined) return null;

  if (typeof constraint === "number") {
    return { value: constraint, isPixel: false };
  }

  const pixelValue = parsePixelValue(constraint);
  if (pixelValue === null) {
    console.warn(
      `[usePanelResize] Invalid pixel value: "${constraint}". Expected format: "300px"`
    );
    return null;
  }

  return { value: pixelValue, isPixel: true };
};

/**
 * Extracts min/max constraints for both panels from range configuration
 */
export interface RangeConstraints {
  firstMin?: FlexConstraintValue;
  firstMax?: FlexConstraintValue;
  secondMin?: FlexConstraintValue;
  secondMax?: FlexConstraintValue;
}

export const extractRangeConstraints = (
  range?: UsePanelResizeParams["range"]
): RangeConstraints => {
  if (!range) return {};

  return {
    firstMin: Array.isArray(range.min) ? range.min[0] : range.min,
    secondMin: Array.isArray(range.min) ? range.min[1] : undefined,
    firstMax: Array.isArray(range.max) ? range.max[0] : range.max,
    secondMax: Array.isArray(range.max) ? range.max[1] : undefined,
  };
};
